// ─────────────────────────────────────────────────────────────────────────────
// Preferred Products Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the preferredProducts slice.
// Loading state is operation-keyed (loading/creating/updating/deleting/
// importing) so concurrent operations never race on a shared boolean.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { IPagination } from 'src/app/shared/models/page.model';
import { IPreferredProduct, PreferredProductFilters } from '../models/preferred-product.models';
import * as preferredProductsActions from './preferred-products.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface PreferredProductsState {
  preferredProducts: IPreferredProduct[];
  metadata: IPagination | null;
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  importing: boolean;
  keyword: string;
  filters: PreferredProductFilters;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialPreferredProductsState: PreferredProductsState = {
  preferredProducts: [],
  metadata: null,
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  importing: false,
  keyword: '',
  filters: { customer_id: null, source: null, create_date_from: null, create_date_to: null },
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialPreferredProductsState,
  on(preferredProductsActions.LoadPreferredProducts, (state) => ({
    ...state,
    loading: true,
  })),
  on(preferredProductsActions.LoadPreferredProductsSuccess, (state, action) => ({
    ...state,
    loading: false,
    preferredProducts: action.response.data,
    metadata: action.response.metadata,
  })),
  on(preferredProductsActions.LoadPreferredProductsError, (state) => ({
    ...state,
    loading: false,
  })),
  on(preferredProductsActions.CreatePreferredProduct, (state) => ({
    ...state,
    creating: true,
  })),
  on(preferredProductsActions.CreatePreferredProductSuccess, (state, action) => ({
    ...state,
    creating: false,
    preferredProducts: [action.preferredProduct, ...state.preferredProducts],
    metadata: state.metadata ? { ...state.metadata, count: state.metadata.count + 1 } : state.metadata,
  })),
  on(preferredProductsActions.CreatePreferredProductError, (state) => ({
    ...state,
    creating: false,
  })),
  on(preferredProductsActions.UpdatePreferredProduct, (state) => ({
    ...state,
    updating: true,
  })),
  on(preferredProductsActions.UpdatePreferredProductSuccess, (state, action) => ({
    ...state,
    updating: false,
    preferredProducts: state.preferredProducts.map((row) => (row.id === action.preferredProduct.id ? action.preferredProduct : row)),
  })),
  on(preferredProductsActions.UpdatePreferredProductError, (state) => ({
    ...state,
    updating: false,
  })),
  on(preferredProductsActions.BulkDeletePreferredProducts, (state) => ({
    ...state,
    deleting: true,
  })),
  on(preferredProductsActions.BulkDeletePreferredProductsSuccess, (state) => ({
    ...state,
    deleting: false,
  })),
  on(preferredProductsActions.BulkDeletePreferredProductsError, (state) => ({
    ...state,
    deleting: false,
  })),
  on(preferredProductsActions.ImportPreferredProducts, (state) => ({
    ...state,
    importing: true,
  })),
  on(preferredProductsActions.ImportPreferredProductsSuccess, (state) => ({
    ...state,
    importing: false,
  })),
  on(preferredProductsActions.ImportPreferredProductsError, (state) => ({
    ...state,
    importing: false,
  })),
  on(preferredProductsActions.SetPreferredProductSearch, (state, action) => ({
    ...state,
    keyword: action.keyword,
  })),
  on(preferredProductsActions.SetPreferredProductFilters, (state, action) => ({
    ...state,
    filters: action.filters,
  })),
);

export function preferredProductsReducer(state: PreferredProductsState | undefined, action: Action) {
  return featureReducer(state, action);
}
